import * as DialogPrimitive from '@radix-ui/react-dialog'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Img } from 'openimg/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { Icon } from '#app/components/ui/icon.tsx'
import { EASE_OUT_QUINT } from '#app/utils/animations.ts'
import { formatEventDateRange } from '#app/utils/event-dates.ts'

export interface AnnouncedEvent {
	id: string
	title: string
	description: string | null
	beginDate: string
	endDate: string | null
	address: string | null
	websiteUrl: string | null
	googleMapsUrl: string | null
	image: { src: string; alt: string | null } | null
	isRunning: boolean
}

const ENTER = { duration: 0.25, ease: EASE_OUT_QUINT } as const
const EXIT = { duration: 0.2, ease: EASE_OUT_QUINT } as const

export function EventAnnouncementModal({ event }: { event: AnnouncedEvent }) {
	const { t, i18n } = useTranslation()
	const shouldReduceMotion = useReducedMotion()
	const [open, setOpen] = useState(false)
	const fetcher = useFetcher<{ success?: boolean }>()
	const isSubmitting = fetcher.state !== 'idle'
	const emailSent = Boolean(fetcher.data?.success)

	const dismissedKey = `event-modal-dismissed:${event.id}`

	// Ref callback with React 19 cleanup schedules the delayed open — the
	// sanctioned alternative to useEffect for after-mount work.
	const scheduleOpen = (node: HTMLElement | null) => {
		if (!node) return
		try {
			if (window.localStorage.getItem(dismissedKey)) return
		} catch {
			return
		}
		const timer = setTimeout(() => setOpen(true), 1200)
		return () => clearTimeout(timer)
	}

	const dismiss = () => {
		setOpen(false)
		try {
			window.localStorage.setItem(dismissedKey, new Date().toISOString())
		} catch {
			// localStorage unavailable — the modal simply reopens next visit
		}
	}

	const dateRange = formatEventDateRange(
		event.beginDate,
		event.endDate,
		i18n.language,
		{ from: t('from'), until: t('until') },
	)

	// Content stagger begins after the 250ms panel entrance, ~50ms apart
	const staggerIn = (order: number) =>
		shouldReduceMotion
			? { initial: false as const }
			: {
					initial: { opacity: 0, y: 12 },
					animate: { opacity: 1, y: 0 },
					transition: {
						duration: 0.3,
						ease: EASE_OUT_QUINT,
						delay: 0.25 + order * 0.05,
					},
				}

	return (
		<>
			<span ref={scheduleOpen} hidden />
			<DialogPrimitive.Root
				open={open}
				onOpenChange={(next) => {
					if (!next) dismiss()
				}}
			>
				<AnimatePresence>
					{open ? (
						<DialogPrimitive.Portal forceMount>
							{/* Overlay and panel are paired: identical duration + easing */}
							<DialogPrimitive.Overlay asChild forceMount>
								<motion.div
									className="fixed inset-0 z-50"
									style={{ background: 'rgba(14, 20, 32, 0.4)' }}
									initial={shouldReduceMotion ? false : { opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={ENTER}
								/>
							</DialogPrimitive.Overlay>
							<DialogPrimitive.Content
								asChild
								forceMount
								aria-describedby={undefined}
								onOpenAutoFocus={(e) => e.preventDefault()}
							>
								<motion.div
									className="fixed top-1/2 left-1/2 z-50 w-[calc(100vw-32px)] max-w-xl overflow-y-auto"
									style={{
										background: 'var(--paper)',
										maxHeight: '85vh',
										// CSS translate centers the panel; motion's transform
										// (opacity/scale/y) composes on top of it
										translate: '-50% -50%',
										willChange: 'transform, opacity',
									}}
									initial={
										shouldReduceMotion
											? false
											: { opacity: 0, scale: 0.97, y: 8 }
									}
									animate={{
										opacity: 1,
										scale: 1,
										y: 0,
										transition: ENTER,
									}}
									exit={{ opacity: 0, scale: 0.97, y: 8, transition: EXIT }}
								>
									<DialogPrimitive.Close
										className="absolute top-4 right-4 z-10 flex h-9 w-9 items-center justify-center"
										style={{
											background: 'var(--paper)',
											border: '1px solid var(--line)',
											borderRadius: '50%',
											cursor: 'pointer',
											color: 'var(--ink)',
										}}
									>
										<Icon name="cross-1" className="h-4 w-4" />
										<span className="sr-only">{t('cancel')}</span>
									</DialogPrimitive.Close>

									{event.image ? (
										<motion.div {...staggerIn(0)} className="overflow-hidden">
											<Img
												src={event.image.src}
												alt={event.image.alt || event.title}
												width={1200}
												height={675}
												fit="cover"
												className="w-full object-cover"
												style={{ aspectRatio: '16/9' }}
											/>
										</motion.div>
									) : null}

									<div className="p-8">
										<motion.div {...staggerIn(1)}>
											<div className="eyebrow mb-3">
												{event.isRunning
													? t('homeNowShowing')
													: t('eventUpcoming')}
											</div>
											<DialogPrimitive.Title asChild>
												<h2
													className="display mb-2"
													style={{ fontSize: 'clamp(26px, 4vw, 38px)' }}
												>
													{event.title}
												</h2>
											</DialogPrimitive.Title>
											{dateRange ? (
												<div className="eyebrow mb-1">{dateRange}</div>
											) : null}
											{event.address ? (
												<div
													className="text-[15px]"
													style={{ color: 'var(--ink-soft)' }}
												>
													{event.address}
												</div>
											) : null}
										</motion.div>

										{event.description ? (
											<motion.p
												{...staggerIn(2)}
												className="mt-4 text-sm leading-relaxed"
												style={{ color: 'var(--ink-soft)' }}
											>
												{event.description}
											</motion.p>
										) : null}

										<motion.div
											{...staggerIn(3)}
											className="mt-6 flex flex-wrap gap-4"
										>
											{event.websiteUrl ? (
												<a
													href={event.websiteUrl}
													target="_blank"
													rel="noopener noreferrer"
													className="btn-gallery-link text-sm"
												>
													{t('website')} ↗
												</a>
											) : null}
											{event.googleMapsUrl ? (
												<a
													href={event.googleMapsUrl}
													target="_blank"
													rel="noopener noreferrer"
													className="btn-gallery-link text-sm"
												>
													{t('eventDirections')} ↗
												</a>
											) : null}
											<a
												href={`/resources/exhibition-calendar/${event.id}`}
												download
												className="btn-gallery-link text-sm"
											>
												{t('eventAddToCalendar')}
											</a>
										</motion.div>

										<motion.div
											{...staggerIn(4)}
											className="mt-8 pt-6"
											style={{ borderTop: '1px solid var(--line)' }}
										>
											{emailSent ? (
												<div>
													<p
														className="text-sm font-medium"
														style={{ color: 'var(--ink)' }}
													>
														✓ {t('eventEmailSuccess')}
													</p>
													<p
														className="mt-1 text-sm"
														style={{ color: 'var(--ink-soft)' }}
													>
														{t('eventEmailSubscribedNote')}
													</p>
												</div>
											) : (
												<fetcher.Form
													method="POST"
													action="/resources/send-event-details"
												>
													<HoneypotInputs />
													<input
														type="hidden"
														name="exhibitionId"
														value={event.id}
													/>
													<p
														className="mb-3 text-sm"
														style={{ color: 'var(--ink-soft)' }}
													>
														{t('eventEmailPrompt')}
													</p>
													<div className="flex gap-2">
														<input
															type="email"
															name="email"
															required
															autoComplete="email"
															disabled={isSubmitting}
															placeholder={t('footerEmailPlaceholder')}
															className="min-w-0 flex-1 rounded-full border px-5 py-3 text-[15px] outline-none"
															style={{
																borderColor: 'var(--line)',
																background: 'var(--paper)',
																fontFamily: 'var(--font-sans)',
															}}
														/>
														<button
															type="submit"
															className="btn-gallery text-sm"
															disabled={isSubmitting}
															style={{ opacity: isSubmitting ? 0.6 : 1 }}
														>
															{isSubmitting ? '...' : t('eventEmailSubmit')}
														</button>
													</div>
												</fetcher.Form>
											)}
										</motion.div>
									</div>
								</motion.div>
							</DialogPrimitive.Content>
						</DialogPrimitive.Portal>
					) : null}
				</AnimatePresence>
			</DialogPrimitive.Root>
		</>
	)
}
